import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Trophy, Undo2, AlertCircle, Check, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { pickleballGameAPI } from './services/pickleballGameApi';
import { SWAP_OPTIONS } from '../scoringConfig';

/**
 * PickleballScoring Component
 *
 * A scoring component for Pickleball matches.
 * Integrated with backend APIs for persistence.
 * Handles only 'scoring' and 'completed' phases (setup/teams handled by GameComponent).
 */
const PickleballScoring = ({
  // Modular flow props (from GameComponent)
  gameCommonId,
  gameSettings,
  participantAInfo,
  participantBInfo,
  // Full team rosters (unnarrowed by any per-match player subset) — temp
  // debug verification + future mid-match player swap UI
  participantATeamRoster = [],
  participantBTeamRoster = [],
  onGameComplete,
  onBack,
  onGamePointsStatus,
  // Event data (for checking event status)
  event,
  // Scorer/viewer mode (from GameComponent)
  isScorer = true,
  // Centralized pause state from GameComponent — when true, disable all scoring inputs.
  // Pause/resume controls and the paused banner are owned by GameComponent.
  paused = false,
  sportType
}) => {
  const { theme } = useTheme();

  // Modular flow: If gameCommonId is provided, game already exists with all data
  const isModularFlow = !!gameCommonId;

  // Debug: Log props on mount
  useEffect(() => {
  }, [event?.status]);

  // Current phase: 'scoring' | 'completed'
  // In modular flow, we only have 'scoring' and 'completed' phases
  const [currentPhase, setCurrentPhase] = useState('scoring');

  // Game state from backend
  const [game, setGame] = useState(null);
  const [gameId, setGameId] = useState(gameCommonId || null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // "Enter Final Score" — lets the scorer skip rally-by-rally scoring and
  // complete the current game (and, if it's the deciding game, the event)
  // by typing in both teams' final scores directly.
  const [showFinalScoreModal, setShowFinalScoreModal] = useState(false);
  const [finalScoreInputs, setFinalScoreInputs] = useState({ a: '', b: '' });
  const [finalScoreSubmitting, setFinalScoreSubmitting] = useState(false);


  // Serve selection state (shown before first rally)
  const [serveChosen, setServeChosen] = useState(false);

  // Holds summary of the just-completed game while waiting for user to start the next one
  const [completedGameSummary, setCompletedGameSummary] = useState(null);

  // Detailed scoring state
  const [detailedScoring, setDetailedScoring] = useState(false);
  const [pendingWinningTeam, setPendingWinningTeam] = useState(null);
  const [scoredByPlayerId, setScoredByPlayerId] = useState(null);
  const [selectedShotType, setSelectedShotType] = useState(null);
  const [errorByPlayerId, setErrorByPlayerId] = useState(null);

  // Mid-match player swap — e.g. an MLP Dreambreaker's roster has 4
  // candidates but only 1 plays singles, or a doubles team's roster is
  // bigger than the 2 on court; the scorer can swap in a different one from
  // the same team without needing to abandon the in-progress game. Persisted
  // via a dedicated endpoint (not the generic update()), so anyone else
  // viewing the scoreboard sees the swap too on their next fetch.
  const [swapModal, setSwapModal] = useState(null); // { team: 1 | 2, oldPlayer: { id, name } }
  const [swapping, setSwapping] = useState(false);

  const handleConfirmSwap = async (newPlayer) => {
    if (!swapModal || !gameId) return;
    setSwapping(true);
    try {
      // Dedicated swap endpoint, not the generic update() — this also
      // corrects server_player_id/even-player/next-server tracking on the
      // backend so an already-serving swapped-out player doesn't keep
      // silently getting credited for rallies after they've been replaced.
      const side = swapModal.team === 1 ? 'A' : 'B';
      const updatedGame = await pickleballGameAPI.swapPlayer(gameId, {
        side,
        oldPlayerId: swapModal.oldPlayer.id,
        newPlayer,
      });
      setGame(updatedGame);
      setSwapModal(null);
      toast.success(`Swapped in ${newPlayer.name}`);
    } catch (err) {
      console.error('[PickleballScoring] Failed to swap player:', err);
      toast.error('Failed to swap player. Please try again.');
    } finally {
      setSwapping(false);
    }
  };

  // MLP Dreambreaker forced swap — real rule: every 4 combined points, BOTH
  // teams swap their current player together, in one action (unlike the
  // manual swapModal above, which is one side at a time). Shows both team's
  // roster pickers in a single non-dismissable modal, plus an escape hatch:
  // if the last point was a scoring mistake, "Undo Last Point" closes this
  // and reverts it via the existing undo flow instead of forcing a swap.
  const [forcedSwapModal, setForcedSwapModal] = useState(null); // { team1OldPlayer, team2OldPlayer, total }
  const [forcedSwapSelections, setForcedSwapSelections] = useState({ team1: null, team2: null });
  const [forcedSwapping, setForcedSwapping] = useState(false);
  // "Skip This Time" just dismisses the current prompt (next 4-point
  // threshold still triggers normally). "Skip Forever" additionally sets
  // this flag, which the trigger effect below checks — once set, no more
  // forced-swap prompts fire for the rest of this game.
  const [skipForcedSwapForGame, setSkipForcedSwapForGame] = useState(false);
  // Tracks the last combined total (team1Score + team2Score) the forced-swap
  // check has already acted on, seeded from the game's score at load time so
  // reloading/reopening an in-progress game doesn't re-pop a swap that
  // already happened in an earlier session.
  const lastForcedSwapTotalRef = useRef(null);
  // How many rallies existed the last time a server was picked (initial
  // pick at game start, OR the mid-game "who serves first for the other
  // team" pick after the first side-out — both go through
  // handleServeSelected). If ralliesCount still matches this, nothing has
  // been scored since, so it's still safe to let the scorer change it.
  const serverPickedAtRalliesRef = useRef(0);

  const handleConfirmForcedSwap = async () => {
    if (!forcedSwapModal || !gameId) return;
    const { team1: newPlayer1, team2: newPlayer2 } = forcedSwapSelections;
    if (!newPlayer1 || !newPlayer2) return;
    setForcedSwapping(true);
    try {
      await pickleballGameAPI.swapPlayer(gameId, {
        side: 'A',
        oldPlayerId: forcedSwapModal.team1OldPlayer.id,
        newPlayer: newPlayer1,
      });
      const updatedGame = await pickleballGameAPI.swapPlayer(gameId, {
        side: 'B',
        oldPlayerId: forcedSwapModal.team2OldPlayer.id,
        newPlayer: newPlayer2,
      });
      setGame(updatedGame);
      toast.success(`Swapped in ${newPlayer1.name} and ${newPlayer2.name}`);
      setForcedSwapModal(null);
      setForcedSwapSelections({ team1: null, team2: null });
    } catch (err) {
      console.error('[PickleballScoring] Failed to swap players:', err);
      toast.error('Failed to swap players. Please try again.');
    } finally {
      setForcedSwapping(false);
    }
  };

  // Escape hatch from the forced-swap modal — undoes the point that
  // triggered it instead of swapping, e.g. when the last point was mis-scored.
  const handleUndoFromForcedSwap = () => {
    setForcedSwapModal(null);
    setForcedSwapSelections({ team1: null, team2: null });
    handleUndo();
  };

  // Dismiss this one prompt only — the next 4-point threshold still triggers
  // the modal again normally.
  const handleSkipForcedSwapOnce = () => {
    setForcedSwapModal(null);
    setForcedSwapSelections({ team1: null, team2: null });
  };

  // Dismiss and suppress every future forced-swap prompt for this game.
  const handleSkipForcedSwapForever = () => {
    setSkipForcedSwapForGame(true);
    setForcedSwapModal(null);
    setForcedSwapSelections({ team1: null, team2: null });
  };

  useEffect(() => {
    if (!game || game.swapOption !== SWAP_OPTIONS.FORCE_ON_TOTAL_POINTS_4) return;
    if (lastForcedSwapTotalRef.current === null) return; // not seeded yet (game still loading)
    if (forcedSwapModal) return;
    if (skipForcedSwapForGame) return;

    const total = (game.team1Score || 0) + (game.team2Score || 0);

    // If the total has dropped below the last handled threshold (e.g. an
    // undo — from this modal's own escape hatch or the regular Undo button
    // elsewhere), rewind the tracker so crossing that same threshold again
    // re-triggers the modal instead of being silently skipped.
    if (total < lastForcedSwapTotalRef.current) {
      lastForcedSwapTotalRef.current = total;
    }

    // The real MLP rule is "swap every 4 points until someone wins" — the
    // combined total naturally exceeds a single team's scoreToWin well
    // before the game actually ends (e.g. 19-21 = combined 40), so the
    // guard must check whether the game itself has been won, not compare
    // the combined total against a single-team target (that comparison
    // was the original bug — it made the trigger stop firing at exactly
    // scoreToWin=21 combined points, long before any real game ends).
    const gameAlreadyWon = game.winByTwo
      ? (game.team1Score >= game.scoreToWin && game.team1Score - game.team2Score >= 2) ||
        (game.team2Score >= game.scoreToWin && game.team2Score - game.team1Score >= 2)
      : (game.team1Score >= game.scoreToWin || game.team2Score >= game.scoreToWin);

    if (
      total > 0 &&
      total % 4 === 0 &&
      total !== lastForcedSwapTotalRef.current &&
      !gameAlreadyWon &&
      game.team1Players?.[0] &&
      game.team2Players?.[0]
    ) {
      lastForcedSwapTotalRef.current = total;
      // Only force the modal open if BOTH teams actually have a bench player
      // to swap in — a modal that can't be dismissed but has nothing to pick
      // for one side would strand the scorer.
      const team1CurrentIds = game.team1Players.map(p => String(p.id));
      const team2CurrentIds = game.team2Players.map(p => String(p.id));
      const team1HasCandidate = participantATeamRoster.some(p => !team1CurrentIds.includes(String(p.id)));
      const team2HasCandidate = participantBTeamRoster.some(p => !team2CurrentIds.includes(String(p.id)));
      if (team1HasCandidate && team2HasCandidate) {
        setForcedSwapModal({ team1OldPlayer: game.team1Players[0], team2OldPlayer: game.team2Players[0], total });
      } else {
        console.warn('[PickleballScoring] Forced Dreambreaker swap skipped — one or both teams have no bench player available');
      }
    }
  }, [game, forcedSwapModal, skipForcedSwapForGame, participantATeamRoster, participantBTeamRoster]);

  const SHOT_TYPES = [
    { key: 'smash', label: 'Smash', category: 'winner' },
    { key: 'ace', label: 'Ace', category: 'winner' },
    { key: 'lob', label: 'Lob', category: 'winner' },
    { key: 'drop_shot', label: 'Drop Shot', category: 'winner' },
    { key: 'volley_winner', label: 'Volley Winner', category: 'winner' },
    { key: 'dink_winner', label: 'Dink Winner', category: 'winner' },
    { key: 'forced_error', label: 'Forced Error', category: 'error' },
    { key: 'unforced_error', label: 'Unforced Error', category: 'error' },
    { key: 'other', label: 'Other', category: 'other' },
  ];

  // Determine if format is doubles (treat anything that isn't 'singles' as doubles)
  const isDoubles = game ? (game.format !== 'singles' || (game.team1Players?.length > 1 || game.team2Players?.length > 1)) : true;

  // Returns 'R', 'L', or null for a player given their team's even-player ID and current score.
  // Even player is on Right when score is even, Left when odd. Partner is the opposite.
  const getPlayerPosition = (playerId, evenPlayerId, teamScore) => {
    if (!evenPlayerId) return null;
    const isEvenPlayer = String(playerId) === String(evenPlayerId);
    const scoreIsEven = (teamScore ?? 0) % 2 === 0;
    if (scoreIsEven) return isEvenPlayer ? 'R' : 'L';
    return isEvenPlayer ? 'L' : 'R';
  };

  // Handle serve selection
  const handleServeSelected = useCallback(async (servingTeam, servingPlayerId) => {
    if (!gameId || saving) return;
    setSaving(true);
    setError(null);
    try {
      const updatedGame = await pickleballGameAPI.setInitialServe(gameId, {
        servingTeam,
        servingPlayerId
      });
      setGame(updatedGame);
      setServeChosen(true);
      // Use the fresh response, not the `game` state closure (stale here —
      // this callback isn't re-created when `game` changes).
      serverPickedAtRalliesRef.current = updatedGame.rallies?.length || 0;
    } catch (err) {
      console.error('[PickleballScoring] Failed to set serve:', err);
      setError('Failed to set serve. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [gameId, saving]);

  const resetDetailState = () => {
    setPendingWinningTeam(null);
    setScoredByPlayerId(null);
    setSelectedShotType(null);
    setErrorByPlayerId(null);
  };

  // Load game on mount
  useEffect(() => {
    const loadGame = async () => {
      // Skip game creation if using modular flow (gameCommonId already provided)
      if (isModularFlow && gameCommonId) {
        // Game already created by GameComponent, just load it
        try {
          setLoading(true);
          const existingGame = await pickleballGameAPI.getById(gameCommonId);
          setGame(existingGame);
          // Seed the forced-swap tracker with the game's current total so
          // reopening an in-progress game doesn't immediately re-trigger a
          // swap for a total that was already handled in an earlier session.
          lastForcedSwapTotalRef.current = (existingGame.team1Score || 0) + (existingGame.team2Score || 0);
          // Seed so a reload right after picking a server (before the next
          // rally) still allows changing it, rather than defaulting to "no".
          serverPickedAtRalliesRef.current = existingGame.rallies?.length || 0;
          setGameId(existingGame.id);

          // Notify parent whether points have been recorded (controls back navigation)
          if (onGamePointsStatus) onGamePointsStatus((existingGame.rallies?.length || 0) > 0);

          // Serve has already been chosen if there's a rally yet OR the
          // backend already recorded a server — setInitialServe persists
          // servingTeam/serverPlayerId immediately (creates the match row),
          // before any rally exists. Checking rally count alone meant a
          // viewer refreshing/polling right after the scorer picked the
          // initial server (but before the first point) would incorrectly
          // keep showing "Waiting for scorer to set serving order...".
          if ((existingGame.rallies?.length || 0) > 0 || existingGame.servingTeam) {
            setServeChosen(true);
          }

          // Set phase based on game status
          if (existingGame.status === 'completed') {
            setCurrentPhase('completed');
          } else {
            setCurrentPhase('scoring');
          }
        } catch (error) {
          console.error('[PickleballScoring] Failed to load game:', error);
          setError('Failed to load game. Please try again.');
        } finally {
          setLoading(false);
        }
        return;
      }

      // Legacy flow (no modular flow) - not used in current implementation
    };

    loadGame();
  }, [isModularFlow, gameCommonId]);

  // Auto-select server for singles when needsServerSelection fires — only 1 player per team so no choice needed
  useEffect(() => {
    if (!game || !serveChosen || !game.needsServerSelection || !isScorer) return;
    const servingPlayers = game.servingTeam === 1 ? game.team1Players : game.team2Players;
    if (servingPlayers?.length === 1) {
      handleServeSelected(game.servingTeam, servingPlayers[0].id);
    }
  }, [game?.needsServerSelection, game?.servingTeam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if current game (within best-of series) is complete
  const isGameComplete = useCallback(() => {
    if (!game) return false;

    const { team1Score, team2Score, scoreToWin, winByTwo } = game;

    if (winByTwo) {
      return (team1Score >= scoreToWin && team1Score - team2Score >= 2) ||
             (team2Score >= scoreToWin && team2Score - team1Score >= 2);
    }
    return team1Score >= scoreToWin || team2Score >= scoreToWin;
  }, [game]);

  // Check if overall match is complete (enough games won) — only ever true
  // AFTER the deciding game's own completeGame call has already landed and
  // incremented team{1,2}GamesWon, so this can't tell the "Complete Game"
  // button whether the game currently being finished IS the deciding one.
  const isMatchComplete = useCallback(() => {
    if (!game) return false;

    const matchesToWin = Math.ceil(game.bestOf / 2);
    return game.team1GamesWon >= matchesToWin || game.team2GamesWon >= matchesToWin;
  }, [game]);

  // Same question, but answerable BEFORE that API call — projects what
  // team{1,2}GamesWon would become if the CURRENT (already-decided, per
  // isGameComplete()) game's winner is credited their win. Used to label the
  // button correctly ("Complete Match" vs "Next Game") ahead of the click,
  // not just after — isMatchComplete() alone always reads false at this
  // point since nothing's been submitted yet, on every best-of, not just
  // best-of-1.
  const willCompleteMatch = useCallback(() => {
    if (!game) return false;
    const matchesToWin = Math.ceil(game.bestOf / 2);
    const team1WinsThisGame = game.team1Score > game.team2Score;
    const projectedTeam1Wins = game.team1GamesWon + (team1WinsThisGame ? 1 : 0);
    const projectedTeam2Wins = game.team2GamesWon + (team1WinsThisGame ? 0 : 1);
    return projectedTeam1Wins >= matchesToWin || projectedTeam2Wins >= matchesToWin;
  }, [game]);

  // Record a rally - call API
  const handleRecordRally = useCallback(async (winningTeam, winningShotPlayerId = null, shotType = null, pointLoserPlayerId = null) => {
    if (!game || !gameId || isGameComplete() || saving) return;

    setSaving(true);
    setError(null);

    try {
      const result = await pickleballGameAPI.recordRally(gameId, {
        winningTeam,
        winningShotPlayerId,
        shotType,
        pointLoserPlayerId,
        isServiceFault: false
      });
      if (result.matchUpdate) {
        setGame(prev => ({ ...prev, ...result.matchUpdate, rallies: [...(prev.rallies || []), result.rally] }));
      } else if (result.game) {
        setGame(result.game);
      }
      resetDetailState();
      // First point scored - disable back navigation
      if ((game.rallies?.length || 0) === 0 && onGamePointsStatus) onGamePointsStatus(true);
    } catch (err) {
      console.error('[PickleballScoring] Failed to record rally:', err);
      if (err.status === 403) {
        toast.error('Another user is scoring this game. Switching to view-only mode.');
        // Reload game to get updated scorer info and re-render as viewer
        try {
          const refreshed = await pickleballGameAPI.getById(gameId);
          setGame(refreshed);
        } catch { /* ignore */ }
      } else {
        setError('Failed to record rally. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }, [game, gameId, isGameComplete, saving, onGamePointsStatus]);

  // Handle team win button click
  const handleTeamWin = useCallback((winningTeam, preSelectedPlayerId = null) => {
    if (!game) return;

    // Determine default winning shot player
    const winningPlayers = winningTeam === 1 ? game.team1Players : game.team2Players;
    let defaultPlayerId = null;

    if (winningTeam === game.servingTeam && game.serverPlayerId) {
      defaultPlayerId = game.serverPlayerId;
    } else if (winningPlayers && winningPlayers.length > 0) {
      defaultPlayerId = winningPlayers[0].id;
    }

    if (detailedScoring) {
      // Detailed mode: set pending winner, user picks shot type + players
      setPendingWinningTeam(winningTeam);
      setScoredByPlayerId(preSelectedPlayerId);
      setSelectedShotType(null);
      setErrorByPlayerId(null);

      // For singles, auto-set the scoring player
      if (!isDoubles) {
        if (winningPlayers?.length > 0) setScoredByPlayerId(winningPlayers[0].id);
      }
    } else {
      // Simple mode, singles only — a doubles button carries the player it was
      // tapped on and records straight through handleRecordRally, so this is
      // only ever reached from the two singles team buttons.
      const playerId = defaultPlayerId || winningPlayers?.[0]?.id || null;
      handleRecordRally(winningTeam, playerId);
    }
  }, [game, handleRecordRally, detailedScoring]);

  // Submit detailed scoring point
  const handleDetailSubmit = useCallback(() => {
    if (pendingWinningTeam == null || !selectedShotType) return;

    const isError = selectedShotType === 'forced_error' || selectedShotType === 'unforced_error';
    let resolvedErrorPlayerId = errorByPlayerId || null;

    // Singles: auto-populate error player from the only player on the losing team
    const gameIsDoubles = game?.participantsType === 'doubles';
    if (!gameIsDoubles && isError && !resolvedErrorPlayerId) {
      const loserTeam = pendingWinningTeam === 1 ? game?.team2Players : game?.team1Players;
      if (loserTeam?.length === 1) resolvedErrorPlayerId = loserTeam[0].id;
    }

    handleRecordRally(
      pendingWinningTeam,
      scoredByPlayerId || null,
      selectedShotType,
      isError ? resolvedErrorPlayerId : null
    );
  }, [pendingWinningTeam, selectedShotType, scoredByPlayerId, errorByPlayerId, game, handleRecordRally]);

  // Record service fault
  const handleServiceFault = useCallback(async () => {
    if (!game || !gameId || saving) return;

    setSaving(true);
    setError(null);

    try {
      const losingTeam = game.servingTeam;
      const winningTeam = losingTeam === 1 ? 2 : 1;

      const result = await pickleballGameAPI.recordRally(gameId, {
        winningTeam,
        winningShotPlayerId: null,
        isServiceFault: true
      });
      if (result.matchUpdate) {
        setGame(prev => ({ ...prev, ...result.matchUpdate, rallies: [...(prev.rallies || []), result.rally] }));
      } else if (result.game) {
        setGame(result.game);
      }
      resetDetailState();
      if ((game.rallies?.length || 0) === 0 && onGamePointsStatus) onGamePointsStatus(true);
    } catch (err) {
      console.error('[PickleballScoring] Failed to record service fault:', err);
      if (err.status === 403) {
        toast.error('Another user is scoring this game. Switching to view-only mode.');
      } else {
        setError('Failed to record fault. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }, [game, gameId, saving, onGamePointsStatus]);

  // Undo last rally - call API
  const handleUndo = useCallback(async () => {
    if (!game || !gameId || (game.rallies?.length || 0) === 0 || saving) return;

    setSaving(true);
    setError(null);

    try {
      const updatedGame = await pickleballGameAPI.undoLastRally(gameId);
      setGame(updatedGame);
      // Last point undone - re-enable back navigation
      if ((game.rallies?.length || 0) === 1 && onGamePointsStatus) onGamePointsStatus(false);
    } catch (err) {
      console.error('[PickleballScoring] Failed to undo:', err);
      if (err.status === 403) {
        toast.error('Another user is scoring this game. Switching to view-only mode.');
      } else {
        setError('Failed to undo. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }, [game, gameId, saving, onGamePointsStatus]);

  // Complete current game (within best-of series) - call API
  // Shared by handleCompleteGame's auto-chain, handleCompleteEvent, and
  // handleSubmitFinalScore's own deciding-game branch — same
  // winner/score-summary shape every caller of onGameComplete needs.
  const buildCompletionResult = (gameData) => {
    const team1Won = gameData.team1GamesWon > gameData.team2GamesWon;
    const winner = team1Won ? gameData.playerA : gameData.playerB;
    return {
      winnerId: winner?.id,
      winnerType: winner?.type || 'user',
      scoreSummary: `${gameData.team1GamesWon}-${gameData.team2GamesWon}`,
      playerAScore: gameData.team1GamesWon,
      playerBScore: gameData.team2GamesWon,
      winner,
      gameId: gameData.id
    };
  };

  const handleCompleteGame = useCallback(async () => {
    if (!game || !gameId || !isGameComplete() || saving) return;

    // Capture current game scores before the API call overwrites game state
    const completedGameNumber = game.currentGame;
    const completedTeam1Score = game.team1Score;
    const completedTeam2Score = game.team2Score;

    setSaving(true);
    setError(null);

    try {
      const response = await pickleballGameAPI.completeGame(gameId);
      const updatedGame = response.game || response; // Handle both response formats
      setGame(updatedGame);

      // Check if match is now complete
      if (updatedGame.status === 'completed') {
        setCurrentPhase('completed');
        // Deciding game of the series — there's nothing left for the scorer
        // to confirm, so finish the tournament match + event in this same
        // click instead of making them look at the winner screen just to
        // press one more button. Same auto-chain handleSubmitFinalScore
        // already does for its own "type the final score" path; onGameComplete
        // (LiveGameTab's handleGameComplete) already catches/toasts its own
        // errors, so a failure here just leaves the scorer on the completed
        // screen where the manual "Complete Event & Continue" button below
        // still works as a retry.
        if (onGameComplete) await onGameComplete(buildCompletionResult(updatedGame));
      } else {
        // Match continues — show between-games screen so user can confirm before starting next game
        setCompletedGameSummary({
          gameNumber: completedGameNumber,
          team1Score: completedTeam1Score,
          team2Score: completedTeam2Score,
          team1GamesWon: updatedGame.team1GamesWon,
          team2GamesWon: updatedGame.team2GamesWon,
          nextGameNumber: updatedGame.currentGame
        });
        resetDetailState();
      }
    } catch (err) {
      console.error('[PickleballScoring] Failed to complete game:', err);
      if (err.status === 403) {
        toast.error('Another user is scoring this game. Switching to view-only mode.');
      } else {
        setError('Failed to complete game. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }, [game, gameId, isGameComplete, saving, onGameComplete]);

  // Complete event — also the manual fallback shown on the completed screen
  // (see `showCompleteEventButton` below) for whenever the auto-chain above
  // didn't run or didn't succeed (e.g. a page reload after the game finished
  // but before the event did, or a network error on the first attempt).
  const handleCompleteEvent = async () => {
    if (!game) {
      console.error('[PickleballScoring] No game data available');
      return;
    }
    if (onGameComplete) {
      await onGameComplete(buildCompletionResult(game));
    }
  };

  // A win-by-2 game ends the instant someone reaches the target with a 2+
  // point lead, so the winner's score can only exceed scoreToWin in a
  // genuine deuce situation (loser also within 1 point of the target) —
  // otherwise the winning score must be exactly scoreToWin. Mirrors the
  // backend's completeMatchWithScore validation exactly.
  const isFinalScoreValid = () => {
    if (!game) return false;
    const a = parseInt(finalScoreInputs.a, 10);
    const b = parseInt(finalScoreInputs.b, 10);
    if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0 || a === b) return false;
    const winner = Math.max(a, b);
    const loser = Math.min(a, b);
    const isDeuce = game.winByTwo && loser >= game.scoreToWin - 1;
    return isDeuce ? (winner - loser === 2) : (winner === game.scoreToWin);
  };

  // Skips rally-by-rally scoring entirely — completes the current game with
  // a directly-typed final score, then (only if this was the deciding game
  // of the best-of series, same condition that reveals the "Complete Event
  // & Continue" button today) immediately chains into event completion too,
  // using the fresh API response rather than the (still-stale) `game` state.
  const handleSubmitFinalScore = async () => {
    if (!gameId || finalScoreSubmitting || !isFinalScoreValid()) return;
    const scoreA = parseInt(finalScoreInputs.a, 10);
    const scoreB = parseInt(finalScoreInputs.b, 10);

    setFinalScoreSubmitting(true);
    try {
      const response = await pickleballGameAPI.completeGameWithScore(gameId, {
        participantAScore: scoreA,
        participantBScore: scoreB,
      });
      const updatedGame = response.game || response;
      setGame(updatedGame);
      setShowFinalScoreModal(false);
      setFinalScoreInputs({ a: '', b: '' });

      if (response.gameComplete) {
        setCurrentPhase('completed');
        if (onGameComplete) {
          await onGameComplete(buildCompletionResult(updatedGame));
        }
      } else {
        setCompletedGameSummary({
          gameNumber: game.currentGame,
          team1Score: scoreA,
          team2Score: scoreB,
          team1GamesWon: updatedGame.team1GamesWon,
          team2GamesWon: updatedGame.team2GamesWon,
          nextGameNumber: updatedGame.currentGame
        });
        resetDetailState();
      }
    } catch (err) {
      console.error('[PickleballScoring] Failed to complete game with final score:', err);
      toast.error(err.message || 'Failed to complete game with that score.');
    } finally {
      setFinalScoreSubmitting(false);
    }
  };

  // Get current server info
  const getServerInfo = useCallback(() => {
    if (!game || !game.servingTeam) return null;

    const servingTeamPlayers = game.servingTeam === 1 ? game.team1Players : game.team2Players;
    const serverPlayer = servingTeamPlayers?.find(p => String(p.id) === String(game.serverPlayerId));
    const teamName = game.servingTeam === 1 ? game.team1Name : game.team2Name;

    return {
      teamName,
      playerName: serverPlayer?.name || teamName,
      serverNumber: game.serverNumber
    };
  }, [game]);


  // Calculate player statistics from rallies
  const calculatePlayerStats = useCallback(() => {
    if (!game) return {};

    // Use all rallies (for completed view, this is across all games)
    const allRallies = game.rallies || [];
    if (allRallies.length === 0) return {};

    const stats = {};
    const allPlayers = [...(game.team1Players || []), ...(game.team2Players || [])];

    // Initialize stats for all players
    allPlayers.forEach(player => {
      const teamNum = game.team1Players?.find(p => p.id === player.id) ? 1 : 2;
      stats[player.id] = {
        name: player.name,
        team: teamNum,
        teamName: teamNum === 1 ? game.team1Name : game.team2Name,
        pointsWhileServing: 0,
        pointsWon: 0,
        ralliesServed: 0,
        ralliesPlayed: 0,
        serviceFaults: 0,
        totalWinningShots: 0
      };
    });

    // Helper to find stats entry by ID (handles int/string mismatches)
    const findStats = (id) => {
      if (id == null) return null;
      return stats[id] || stats[parseInt(id)] || stats[String(id)] || null;
    };

    // Calculate stats from rallies
    allRallies.forEach(rally => {
      allPlayers.forEach(player => {
        if (stats[player.id]) {
          stats[player.id].ralliesPlayed++;
          if (rally.winningTeam === stats[player.id].team && !rally.isServiceFault) {
            stats[player.id].pointsWon++;
          }
        }
      });

      const serverStats = findStats(rally.serverPlayerId);
      if (serverStats) {
        serverStats.ralliesServed++;
        if (rally.isServiceFault) {
          serverStats.serviceFaults++;
        } else if (rally.winningTeam === serverStats.team) {
          serverStats.pointsWhileServing++;
        }
      }

      const winnerStats = findStats(rally.winningShotPlayerId);
      if (winnerStats) {
        winnerStats.totalWinningShots++;
      }
    });

    // Calculate serving efficiency
    Object.keys(stats).forEach(playerId => {
      const playerStats = stats[playerId];
      playerStats.servingEfficiency = playerStats.ralliesServed > 0
        ? Math.round((playerStats.pointsWhileServing / playerStats.ralliesServed) * 100)
        : 0;
    });

    return stats;
  }, [game]);

  // Refresh game data from server
  const handleRefresh = useCallback(async () => {
    if (!gameId) return;
    try {
      setSaving(true);
      const refreshedGame = await pickleballGameAPI.getById(gameId);
      setGame(refreshedGame);
      toast.success('Game refreshed');
    } catch (err) {
      console.error('[PickleballScoring] Failed to refresh:', err);
      toast.error('Failed to refresh');
    } finally {
      setSaving(false);
    }
  }, [gameId]);

  // renderSetupPhase removed - now handled by MatchSettingsComponent in GameComponent
  // renderTeamsPhase removed - now handled by TeamFormation in GameComponent

  // Render Scoring Phase
  const renderScoringPhase = () => {
    if (!game) return null;

    // Between-games screen — shown after a game completes but before the next starts
    if (completedGameSummary) {
      const team1Won = completedGameSummary.team1Score > completedGameSummary.team2Score;
      const winnerName = team1Won ? game.team1Name : game.team2Name;
      return (
        <div className="space-y-4">
          <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center space-y-3`}>
            <Trophy className="w-12 h-12 mx-auto text-yellow-500" />
            <div className={`text-lg font-bold ${theme.text}`}>
              Game {completedGameSummary.gameNumber} Complete
            </div>
            <div className={`text-3xl font-bold ${theme.text}`}>
              {completedGameSummary.team1Score} – {completedGameSummary.team2Score}
            </div>
            <div className={`text-sm ${theme.textMuted}`}>
              <span className="font-semibold">{winnerName}</span> wins this game
            </div>
            <div className={`text-sm ${theme.textMuted}`}>
              Series: {game.team1Name} <span className="font-bold">{completedGameSummary.team1GamesWon}</span>
              {' – '}
              <span className="font-bold">{completedGameSummary.team2GamesWon}</span> {game.team2Name}
            </div>
          </div>

          {isScorer && (
            <button
              onClick={() => {
                setCompletedGameSummary(null);
                setServeChosen(false);
                serverPickedAtRalliesRef.current = 0;
              }}
              className={`w-full ${theme.accent} ${theme.accentHover} text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2`}
            >
              <RefreshCw className="w-5 h-5" />
              Start Game {completedGameSummary.nextGameNumber}
            </button>
          )}
        </div>
      );
    }

    const gameComplete = isGameComplete();
    const serverInfo = getServerInfo();
    const ralliesCount = game.rallies?.length || 0;

    return (
      <div className="space-y-4 min-h-[60vh]">
        {/* Game Header */}
        <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
          <div className="text-center">
            <div className={`text-sm ${theme.textMuted} mb-1`}>
              Game {game.currentGame} of {game.bestOf}
            </div>
            <div className={`text-lg font-semibold ${theme.text}`}>
              {game.team1Name} ({game.team1GamesWon}) vs {game.team2Name} ({game.team2GamesWon})
            </div>
          </div>

          {/* Live indicator — paused state is shown by the centralized PausedBanner */}
          {!paused && (
            <div className="flex justify-center mt-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                LIVE
              </span>
            </div>
          )}
        </div>

        {/* Score Display */}
        <div className="grid grid-cols-2 gap-4">
          {/* Team 1 Score */}
          <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center ${
            game.servingTeam === 1 ? 'ring-2 ring-green-500' : ''
          }`}>
            <div className={`text-sm ${theme.textMuted} mb-2 truncate`}>{game.team1Name}</div>
            <div className={`text-6xl font-bold ${theme.text} mb-2`}>{game.team1Score}</div>
            <div className={`text-sm ${theme.textMuted}`}>
              Games: {game.team1GamesWon}
            </div>
            {game.servingTeam === 1 && (
              <div className="mt-2 text-sm font-medium text-green-600">SERVING</div>
            )}
            {game.swappable && game.swapOption !== SWAP_OPTIONS.FORCE_ON_TOTAL_POINTS_4 && isScorer && !gameComplete && participantATeamRoster.length > (game.team1Players?.length || 0) && (
              <div className="mt-3 flex flex-col items-center gap-1">
                {(game.team1Players || []).map(player => (
                  <button
                    key={player.id}
                    type="button"
                    onClick={() => setSwapModal({ team: 1, oldPlayer: player })}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border ${theme.border} ${theme.textMuted} hover:opacity-80`}
                  >
                    <RefreshCw className="w-3 h-3" /> Swap{isDoubles ? ` ${player.name?.split(' ')[0] || player.name}` : ' player'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Team 2 Score */}
          <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center ${
            game.servingTeam === 2 ? 'ring-2 ring-blue-500' : ''
          }`}>
            <div className={`text-sm ${theme.textMuted} mb-2 truncate`}>{game.team2Name}</div>
            <div className={`text-6xl font-bold ${theme.text} mb-2`}>{game.team2Score}</div>
            <div className={`text-sm ${theme.textMuted}`}>
              Games: {game.team2GamesWon}
            </div>
            {game.servingTeam === 2 && (
              <div className="mt-2 text-sm font-medium text-blue-600">SERVING</div>
            )}
            {game.swappable && game.swapOption !== SWAP_OPTIONS.FORCE_ON_TOTAL_POINTS_4 && isScorer && !gameComplete && participantBTeamRoster.length > (game.team2Players?.length || 0) && (
              <div className="mt-3 flex flex-col items-center gap-1">
                {(game.team2Players || []).map(player => (
                  <button
                    key={player.id}
                    type="button"
                    onClick={() => setSwapModal({ team: 2, oldPlayer: player })}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border ${theme.border} ${theme.textMuted} hover:opacity-80`}
                  >
                    <RefreshCw className="w-3 h-3" /> Swap{isDoubles ? ` ${player.name?.split(' ')[0] || player.name}` : ' player'}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pickleball Score Notation: serving-receiving-server# */}
        {serveChosen && serverInfo && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-3 text-center`}>
            <div className={`text-xs ${theme.textMuted} mb-1`}>Score</div>
            <div className={`text-3xl font-bold tracking-widest ${theme.text}`}>
              {game.servingTeam === 1 ? game.team1Score : game.team2Score}
              {' – '}
              {game.servingTeam === 1 ? game.team2Score : game.team1Score}
              {' – '}
              {serverInfo.serverNumber ?? 1}
            </div>
            <div className={`text-xs ${theme.textMuted} mt-1`}>
              Server: <span className="font-semibold">{serverInfo.playerName}</span>
              {isDoubles && serverInfo.playerName !== serverInfo.teamName && (
                <span> ({serverInfo.teamName})</span>
              )}
              {isScorer && ralliesCount === serverPickedAtRalliesRef.current && (
                <div>
                  <button
                    onClick={() => setServeChosen(false)}
                    className="text-blue-500 hover:underline font-medium"
                  >
                    Change Server
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Serve Selection (before first rally, or redoing a pick via
            "Change") — scorer only. serverPickedAtRalliesRef.current === 0
            means this is the game's very first server (no side-out could
            have happened yet), so both teams are still up for grabs.
            Otherwise this is redoing the OTHER team's first-ever server
            (picked mid-game after a side-out) — same single-team picker as
            the automatic needsServerSelection prompt below, just reopened
            manually. */}
        {isScorer && !serveChosen && !gameComplete && serverPickedAtRalliesRef.current > 0 && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-3`}>
            <h3 className={`${theme.text} font-semibold text-center`}>
              Who serves first for {game.servingTeam === 1 ? game.team1Name : game.team2Name}?
            </h3>
            <div className="flex flex-col gap-2">
              {(game.servingTeam === 1 ? game.team1Players : game.team2Players)?.map(player => {
                const evenId = game.servingTeam === 1 ? game.team1EvenPlayerId : game.team2EvenPlayerId;
                const teamScore = game.servingTeam === 1 ? game.team1Score : game.team2Score;
                const position = getPlayerPosition(player.id, evenId, teamScore);
                const firstName = player.name?.split(' ')[0] || player.name || '';
                return (
                  <button
                    key={player.id}
                    onClick={() => handleServeSelected(game.servingTeam, player.id)}
                    disabled={saving || paused}
                    className={`w-full py-5 px-4 rounded-lg font-semibold transition-transform active:scale-95 ${
                      game.servingTeam === 1
                        ? 'bg-green-600 hover:bg-green-700 active:bg-green-800'
                        : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                    } text-white disabled:opacity-50`}
                  >
                    <span className="sm:hidden">{firstName.slice(0, 7)}</span>
                    <span className="hidden sm:inline">{firstName}</span>
                    {position && <span className="ml-2 text-xs font-normal opacity-75">({position})</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {isScorer && !serveChosen && !gameComplete && serverPickedAtRalliesRef.current === 0 && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-4`}>
            <h3 className={`${theme.text} font-semibold text-center`}>Who is serving first?</h3>
            {isDoubles ? (
              <div className="grid grid-cols-2 gap-4">
                {/* Team 1 column */}
                <div className="flex flex-col gap-3">
                  <div className="text-xs font-semibold text-center uppercase tracking-wide text-green-500 truncate">
                    {game.team1Name}
                  </div>
                  {(game.team1Players || []).map(player => {
                    const firstName = player.name?.split(' ')[0] || player.name || '';
                    return (
                      <button
                        key={player.id}
                        onClick={() => handleServeSelected(1, player.id)}
                        disabled={saving || paused}
                        className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 active:scale-95 text-white py-5 rounded-lg font-semibold transition-transform disabled:opacity-50"
                      >
                        <span className="sm:hidden">{firstName.slice(0, 7)}</span>
                        <span className="hidden sm:inline">{firstName}</span>
                      </button>
                    );
                  })}
                </div>
                {/* Team 2 column */}
                <div className="flex flex-col gap-3">
                  <div className="text-xs font-semibold text-center uppercase tracking-wide text-blue-400 truncate">
                    {game.team2Name}
                  </div>
                  {(game.team2Players || []).map(player => {
                    const firstName = player.name?.split(' ')[0] || player.name || '';
                    return (
                      <button
                        key={player.id}
                        onClick={() => handleServeSelected(2, player.id)}
                        disabled={saving || paused}
                        className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-95 text-white py-5 rounded-lg font-semibold transition-transform disabled:opacity-50"
                      >
                        <span className="sm:hidden">{firstName.slice(0, 7)}</span>
                        <span className="hidden sm:inline">{firstName}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleServeSelected(1, game.team1Players?.[0]?.id)}
                  disabled={saving || paused}
                  className="py-4 px-4 rounded-lg font-semibold text-lg transition-colors bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                >
                  {game.team1Name}
                </button>
                <button
                  onClick={() => handleServeSelected(2, game.team2Players?.[0]?.id)}
                  disabled={saving || paused}
                  className="py-4 px-4 rounded-lg font-semibold text-lg transition-colors bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                >
                  {game.team2Name}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Skip rally-by-rally scoring and enter a final score directly —
            only available before any point has been recorded */}
        {isScorer && !gameComplete && ralliesCount === 0 && (
          <button
            onClick={() => setShowFinalScoreModal(true)}
            className={`w-full text-sm py-2 rounded-lg border ${theme.border} ${theme.textMuted} hover:${theme.text}`}
          >
            Enter Final Score Instead
          </button>
        )}

        {/* Mid-game: server selection needed after side-out (position unknown) */}
        {isScorer && serveChosen && game?.needsServerSelection && !gameComplete && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-3`}>
            <h3 className={`${theme.text} font-semibold text-center`}>
              Who serves first for {game.servingTeam === 1 ? game.team1Name : game.team2Name}?
            </h3>
            <div className="flex flex-col gap-2">
              {(game.servingTeam === 1 ? game.team1Players : game.team2Players)?.map(player => {
                const evenId = game.servingTeam === 1 ? game.team1EvenPlayerId : game.team2EvenPlayerId;
                const teamScore = game.servingTeam === 1 ? game.team1Score : game.team2Score;
                const position = getPlayerPosition(player.id, evenId, teamScore);
                const firstName = player.name?.split(' ')[0] || player.name || '';
                return (
                  <button
                    key={player.id}
                    onClick={() => handleServeSelected(game.servingTeam, player.id)}
                    disabled={saving || paused}
                    className={`w-full py-5 px-4 rounded-lg font-semibold transition-transform active:scale-95 ${
                      game.servingTeam === 1
                        ? 'bg-green-600 hover:bg-green-700 active:bg-green-800'
                        : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                    } text-white disabled:opacity-50`}
                  >
                    <span className="sm:hidden">{firstName.slice(0, 7)}</span>
                    <span className="hidden sm:inline">{firstName}</span>
                    {position && <span className="ml-2 text-xs font-normal opacity-75">({position})</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Viewer waiting for scorer to select serve */}
        {!isScorer && !serveChosen && !gameComplete && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
            <p className={`${theme.textMuted}`}>Waiting for scorer to set serving order...</p>
          </div>
        )}

        {/* Viewer waiting for mid-game server selection */}
        {!isScorer && serveChosen && game?.needsServerSelection && !gameComplete && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
            <p className={`${theme.textMuted}`}>Waiting for scorer to select server...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Detailed Scoring Toggle — scorer only */}
        {isScorer && serveChosen && !gameComplete && (
          <label className={`flex items-center gap-2 text-sm ${theme.textMuted} cursor-pointer select-none`}>
            <input
              type="checkbox"
              checked={detailedScoring}
              onChange={e => { setDetailedScoring(e.target.checked); resetDetailState(); }}
              className="rounded"
            />
            <span>Enable Detailed Scoring</span>
          </label>
        )}

        {/* Action Buttons */}
        {!serveChosen || game?.needsServerSelection ? null : gameComplete ? (isScorer ? (
          <button
            onClick={handleCompleteGame}
            disabled={saving || paused}
            className={`w-full ${theme.accent} ${theme.accentHover} py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2`}
          >
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            ) : willCompleteMatch() ? (
              <>
                <Trophy className="w-5 h-5" />
                Complete Match
              </>
            ) : (
              <>
                Next Game
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        ) : null) : (
          <div className="space-y-4">
            {/* Player buttons */}
            {isDoubles ? (
          /* Doubles — one button per player, two columns.
             In detail mode routes through handleTeamWin so the detail panel activates;
             in simple mode records the rally immediately with the tapped player's ID. */
          <div className="grid grid-cols-2 gap-3">
            {/* Team A column */}
            <div className="flex flex-col gap-3">
              <div className={`text-xs font-semibold text-center uppercase tracking-wide text-green-500 truncate`}>
                {game.team1Name}
              </div>
              {(game.team1Players || []).map(player => {
                const firstName = player.name?.split(' ')[0] || player.name || '';
                const isServer = isDoubles && String(player.id) === String(game.serverPlayerId);
                const position = getPlayerPosition(player.id, game.team1EvenPlayerId, game.team1Score);
                return (
                  <button
                    key={player.id}
                    onClick={() => detailedScoring ? handleTeamWin(1, player.id) : handleRecordRally(1, player.id)}
                    disabled={!isScorer || paused}
                    className={`w-full bg-green-600 hover:bg-green-700 active:bg-green-800 active:scale-95 text-white py-5 rounded-lg font-semibold transition-transform disabled:opacity-50 ${isServer ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-transparent' : ''}`}
                  >
                    <span className="sm:hidden">{firstName.slice(0, 7)}</span>
                    <span className="hidden sm:inline">{firstName}</span>
                    {position && <span className="ml-2 text-xs font-normal opacity-75">({position})</span>}
                  </button>
                );
              })}
            </div>
            {/* Team B column */}
            <div className="flex flex-col gap-3">
              <div className={`text-xs font-semibold text-center uppercase tracking-wide text-blue-400 truncate`}>
                {game.team2Name}
              </div>
              {(game.team2Players || []).map(player => {
                const firstName = player.name?.split(' ')[0] || player.name || '';
                const isServer = isDoubles && String(player.id) === String(game.serverPlayerId);
                const position = getPlayerPosition(player.id, game.team2EvenPlayerId, game.team2Score);
                return (
                  <button
                    key={player.id}
                    onClick={() => detailedScoring ? handleTeamWin(2, player.id) : handleRecordRally(2, player.id)}
                    disabled={!isScorer || paused}
                    className={`w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-95 text-white py-5 rounded-lg font-semibold transition-transform disabled:opacity-50 ${isServer ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-transparent' : ''}`}
                  >
                    <span className="sm:hidden">{firstName.slice(0, 7)}</span>
                    <span className="hidden sm:inline">{firstName}</span>
                    {position && <span className="ml-2 text-xs font-normal opacity-75">({position})</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* Singles — two team buttons */
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleTeamWin(1)}
              disabled={!isScorer || saving || paused}
              className={`bg-green-600 hover:bg-green-700 active:bg-green-800 active:scale-95 text-white py-6 rounded-lg transition-transform disabled:opacity-50 ${game.servingTeam === 1 ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-transparent' : ''}`}
            >
              <div className="flex flex-col items-center gap-0.5">
                <span className="font-semibold text-lg">{game.team1Name?.split(' ')[0] || game.team1Name}</span>
                <span className="text-xs font-normal opacity-70">tap to score</span>
              </div>
            </button>
            <button
              onClick={() => handleTeamWin(2)}
              disabled={!isScorer || saving || paused}
              className={`bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-95 text-white py-6 rounded-lg transition-transform disabled:opacity-50 ${game.servingTeam === 2 ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-transparent' : ''}`}
            >
              <div className="flex flex-col items-center gap-0.5">
                <span className="font-semibold text-lg">{game.team2Name?.split(' ')[0] || game.team2Name}</span>
                <span className="text-xs font-normal opacity-70">tap to score</span>
              </div>
            </button>
          </div>
          )}

            {/* Detail scoring panel — visible as soon as detailedScoring is on */}
            {detailedScoring && (() => {
              const SHOT_COLORS = {
                ace:             { active: 'bg-purple-600 text-white ring-2 ring-purple-300', idle: 'bg-purple-100 text-purple-800 hover:bg-purple-200' },
                smash:           { active: 'bg-red-600 text-white ring-2 ring-red-300',      idle: 'bg-red-100 text-red-800 hover:bg-red-200' },
                lob:             { active: 'bg-sky-600 text-white ring-2 ring-sky-300',       idle: 'bg-sky-100 text-sky-800 hover:bg-sky-200' },
                drop_shot:       { active: 'bg-teal-600 text-white ring-2 ring-teal-300',     idle: 'bg-teal-100 text-teal-800 hover:bg-teal-200' },
                volley_winner:   { active: 'bg-emerald-600 text-white ring-2 ring-emerald-300', idle: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' },
                dink_winner:     { active: 'bg-indigo-600 text-white ring-2 ring-indigo-300', idle: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200' },
                forced_error:    { active: 'bg-orange-600 text-white ring-2 ring-orange-300', idle: 'bg-orange-100 text-orange-800 hover:bg-orange-200' },
                unforced_error:  { active: 'bg-amber-600 text-white ring-2 ring-amber-300',   idle: 'bg-amber-100 text-amber-800 hover:bg-amber-200' },
                other:           { active: 'bg-gray-600 text-white ring-2 ring-gray-300',     idle: 'bg-gray-200 text-gray-700 hover:bg-gray-300' },
              };
              return (
                <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-4`}>
                  {/* Header — only when a player has been tapped */}
                  {pendingWinningTeam != null && (
                    <div className="flex items-center justify-between">
                      <h3 className={`${theme.text} font-semibold`}>
                        {(() => {
                          const players = pendingWinningTeam === 1 ? game.team1Players : game.team2Players;
                          const scorer = scoredByPlayerId ? players?.find(p => String(p.id) === String(scoredByPlayerId)) : null;
                          return scorer?.name || (pendingWinningTeam === 1 ? game.team1Name : game.team2Name);
                        })()} Won Point
                      </h3>
                      <button onClick={resetDetailState} className={`text-sm ${theme.textMuted} hover:${theme.text}`}>
                        Cancel
                      </button>
                    </div>
                  )}

                  {/* Step 1: Who scored? (doubles only) */}
                  {isDoubles && pendingWinningTeam != null && scoredByPlayerId == null && !['forced_error', 'unforced_error', 'other'].includes(selectedShotType) && (
                    <div>
                      <div className={`text-sm font-medium ${theme.textMuted} mb-2`}>Who won the point?</div>
                      <div className="grid grid-cols-2 gap-2">
                        {(pendingWinningTeam === 1 ? game.team1Players : game.team2Players)?.map(player => (
                          <button
                            key={player.id}
                            onClick={() => { setScoredByPlayerId(player.id); setSelectedShotType(null); setErrorByPlayerId(null); }}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                              String(scoredByPlayerId) === String(player.id)
                                ? 'bg-green-600 text-white'
                                : `${theme.secondary} ${theme.secondaryHover} ${theme.text}`
                            }`}
                          >
                            {player.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Winners */}
                  <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-1.5`}>Winning Shot Type</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                    {SHOT_TYPES.filter(s => s.category === 'winner').map(shot => {
                      const colors = SHOT_COLORS[shot.key];
                      const isAce = shot.key === 'ace';
                      const disabled = isAce
                        ? (pendingWinningTeam != null && pendingWinningTeam !== game.servingTeam)
                        : pendingWinningTeam == null;
                      return (
                        <button
                          key={shot.key}
                          disabled={disabled}
                          onClick={() => {
                            if (isAce) {
                              handleRecordRally(game.servingTeam, game.serverPlayerId, shot.key, null);
                            } else {
                              handleRecordRally(pendingWinningTeam, isDoubles ? (scoredByPlayerId || null) : null, shot.key, null);
                            }
                          }}
                          className={`py-2 px-2 rounded-lg text-sm font-semibold transition-all ${
                            selectedShotType === shot.key
                              ? colors.active
                              : disabled
                                ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400'
                                : colors.idle
                          }`}
                        >
                          {shot.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Errors */}
                  <div className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-1.5`}>Opponent Error Type</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                    <button
                      onClick={handleServiceFault}
                      disabled={saving}
                      className="py-2 px-2 rounded-lg text-sm font-semibold transition-all bg-rose-100 text-rose-800 hover:bg-rose-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Service Fault
                    </button>
                    {SHOT_TYPES.filter(s => s.category === 'error' || s.category === 'other').map(shot => {
                      const colors = SHOT_COLORS[shot.key];
                      const needsErrorPlayer = shot.key === 'forced_error' || shot.key === 'unforced_error' || shot.key === 'other';
                      return (
                        <button
                          key={shot.key}
                          disabled={pendingWinningTeam == null}
                          onClick={() => {
                            if (!isDoubles) {
                              const loserTeam = pendingWinningTeam === 1 ? game?.team2Players : game?.team1Players;
                              const errorPlayerId = needsErrorPlayer && loserTeam?.length === 1 ? loserTeam[0].id : null;
                              handleRecordRally(pendingWinningTeam, null, shot.key, needsErrorPlayer ? errorPlayerId : null);
                            } else {
                              setSelectedShotType(shot.key);
                              setErrorByPlayerId(null);
                            }
                          }}
                          className={`py-2 px-2 rounded-lg text-sm font-semibold transition-all ${
                            pendingWinningTeam == null
                              ? 'opacity-40 cursor-not-allowed ' + colors.idle
                              : selectedShotType === shot.key ? colors.active : colors.idle
                          }`}
                        >
                          {shot.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Step 3: Who made the error? (doubles only) */}
                  {isDoubles && pendingWinningTeam != null && selectedShotType && ['forced_error', 'unforced_error', 'other'].includes(selectedShotType) && (
                    <div>
                      <div className={`text-sm font-medium ${theme.textMuted} mb-2`}>Who made the error?</div>
                      <div className="grid grid-cols-2 gap-2">
                        {(pendingWinningTeam === 1 ? game.team2Players : game.team1Players)?.map(player => (
                          <button
                            key={player.id}
                            onClick={() => handleRecordRally(pendingWinningTeam, scoredByPlayerId || null, selectedShotType, player.id)}
                            disabled={saving}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${theme.secondary} ${theme.secondaryHover} ${theme.text}`}
                          >
                            {player.name}
                            <span className={`block text-xs ${theme.textMuted}`}>
                              {pendingWinningTeam === 1 ? game.team2Name : game.team1Name}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Secondary Actions — only after serve chosen, scorer only, hidden when paused */}
        {isScorer && serveChosen && !gameComplete && !pendingWinningTeam && !paused && !game?.needsServerSelection && (
          <div className="flex">
            <button
              onClick={handleUndo}
              disabled={ralliesCount === 0 || saving}
              className={`w-full ${theme.secondary} ${theme.secondaryHover} ${theme.text} py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50`}
            >
              <Undo2 className="w-4 h-4" />
              Undo Last
            </button>
          </div>
        )}

        {/* Undo button when game is complete — scorer only */}
        {isScorer && gameComplete && (
          <button
            onClick={handleUndo}
            disabled={ralliesCount === 0 || saving}
            className={`w-full ${theme.secondary} ${theme.secondaryHover} ${theme.text} py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50`}
          >
            <Undo2 className="w-4 h-4" />
            Undo Last Point
          </button>
        )}

        {/* Saving indicator - fixed height to prevent layout shift */}
        <div className="h-6 flex items-center justify-center">
          {saving && (
            <div className={`text-sm ${theme.textMuted} flex items-center gap-2`}>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
              Saving...
            </div>
          )}
        </div>

        {/* Rally History */}
        {ralliesCount > 0 && (
          <details className={`${theme.card} rounded-lg border ${theme.border}`}>
            <summary className={`${theme.text} p-4 cursor-pointer font-medium`}>
              Rally History ({ralliesCount} rallies)
            </summary>
            <div className="p-4 pt-0 max-h-48 overflow-y-auto">
              {[...(game.rallies || [])].reverse().slice(0, 10).map((rally, idx) => {
                // Include the full team rosters, not just the currently
                // playing team1Players/team2Players — after a mid-match
                // swap, an older rally's winningShotPlayerId still points at
                // whoever was swapped out, and they're no longer in
                // team1Players/team2Players. Without their name here, the
                // lookup below fails and falls back to game.team1Name /
                // game.team2Name (the *current* player), silently
                // relabeling that historical rally with the wrong name.
                const allPlayers = [...(game.team1Players || []), ...(game.team2Players || []), ...participantATeamRoster, ...participantBTeamRoster];
                // Player ids are UUIDs, not integers — parseInt() truncates
                // at the first non-numeric character, so e.g. "019f0e50-..."
                // and "019ec749-..." (a common shared numeric-prefix pattern
                // in UUIDs) both evaluate to the same number and collide.
                const findPlayer = (id) => allPlayers.find(p => String(p.id) === String(id))?.name;
                const winnerPlayer = rally.winningShotPlayerId ? findPlayer(rally.winningShotPlayerId) : null;
                const errorPlayer = rally.pointLoserPlayerId ? findPlayer(rally.pointLoserPlayerId) : null;
                const serverPlayer = rally.serverPlayerId ? findPlayer(rally.serverPlayerId) : null;
                const isErrorType = rally.shotType === 'forced_error' || rally.shotType === 'unforced_error' || rally.shotType === 'other';
                // In rally scoring every win scores; in sideout scoring only the serving team scores
                const pointScored = !rally.isServiceFault && (rally.winningTeam === rally.servingTeam || game.isRally);
                const scoreAfter1 = rally.team1ScoreBefore + (pointScored && rally.winningTeam === 1 ? 1 : 0);
                const scoreAfter2 = rally.team2ScoreBefore + (pointScored && rally.winningTeam === 2 ? 1 : 0);
                return (
                  <div key={rally.id} className={`text-sm py-1.5 border-b ${theme.border} last:border-0`}>
                    <div>
                      <span className={theme.textMuted}>#{ralliesCount - idx}: </span>
                      <span className={rally.isServiceFault ? 'text-orange-500' : rally.winningTeam === 1 ? 'text-green-600' : 'text-blue-600'}>
                        {rally.isServiceFault
                          ? 'Service Fault'
                          : `${winnerPlayer || (rally.winningTeam === 1 ? game.team1Name : game.team2Name)} won`}
                      </span>
                      <span className={theme.textMuted}>
                        {rally.isServiceFault
                          ? serverPlayer ? ` by ${serverPlayer}` : ''
                          : isErrorType
                            ? ` — ${rally.shotType === 'other' ? 'other error' : rally.shotType.replace(/_/g, ' ')}${errorPlayer ? ` by ${errorPlayer}` : ''}`
                            : rally.shotType
                              ? ` — ${winnerPlayer ? `${winnerPlayer} ` : ''}(${rally.shotType.replace(/_/g, ' ')})`
                              : ''}
                      </span>
                      <span className={`${theme.textMuted}`}> — {scoreAfter1}-{scoreAfter2}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        )}
      </div>
    );
  };

  // Render Completed Phase
  const renderCompletedPhase = () => {
    if (!game) return null;

    const team1Won = game.team1GamesWon > game.team2GamesWon;
    const winnerTeamName = team1Won ? game.team1Name : game.team2Name;
    const loserTeamName = team1Won ? game.team2Name : game.team1Name;
    const stats = calculatePlayerStats();

    // Check completion status
    const isGameCompleted = game?.status === 'completed';
    const isEventCompleted = event?.status === 'completed';
    const showCompleteEventButton = isGameCompleted && !isEventCompleted && isScorer;

    // Calculate total points
    const totalRallies = game.rallies?.length || 0;

    return (
      <div className="space-y-6">
        {/* Read-only indicator for completed events */}
        {isEventCompleted && (
          <div className={`${theme.backgroundSecondary} rounded-lg border ${theme.border} p-4 text-center`}>
            <div className={`text-sm ${theme.textMuted} flex items-center justify-center gap-2`}>
              <Check className="w-4 h-4 text-green-500" />
              Event Completed - View Only
            </div>
          </div>
        )}

        {/* Winner Banner */}
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
          <Trophy className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
          <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>{winnerTeamName} Wins!</h2>
          <div className={`text-lg ${theme.textMuted} mb-2`}>
            defeated <span className={theme.text}>{loserTeamName}</span>
          </div>
          <div className={`text-sm ${theme.textMuted} mt-1`}>
            Best of {game.bestOf} | {isDoubles ? 'Doubles' : 'Singles'} |
            Games: {team1Won
              ? `${game.team1GamesWon} - ${game.team2GamesWon}`
              : `${game.team2GamesWon} - ${game.team1GamesWon}`}
          </div>
        </div>

        {/* Match Summary */}
        <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
          <h3 className={`${theme.text} font-semibold mb-4`}>Match Summary</h3>

          {/* Teams Header */}
          <div className={`grid grid-cols-3 gap-2 mb-4 pb-3 border-b ${theme.border}`}>
            <div className={`text-center ${team1Won ? 'font-semibold' : ''}`}>
              <div className={`${theme.text} ${team1Won ? 'text-green-600' : ''}`}>
                {game.team1Name}
                {team1Won && <Trophy className="w-4 h-4 inline ml-1 text-yellow-500" />}
              </div>
              <div className={`text-2xl font-bold ${team1Won ? 'text-green-600' : theme.text}`}>
                {game.team1GamesWon}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>games won</div>
              {game.team1Players?.length > 0 && (
                <div className={`text-xs ${theme.textMuted} mt-1`}>
                  {game.team1Players.map(p => p.name).join(', ')}
                </div>
              )}
            </div>
            <div className={`text-center ${theme.textMuted} flex items-center justify-center text-xl`}>
              VS
            </div>
            <div className={`text-center ${!team1Won ? 'font-semibold' : ''}`}>
              <div className={`${theme.text} ${!team1Won ? 'text-green-600' : ''}`}>
                {game.team2Name}
                {!team1Won && <Trophy className="w-4 h-4 inline ml-1 text-yellow-500" />}
              </div>
              <div className={`text-2xl font-bold ${!team1Won ? 'text-green-600' : theme.text}`}>
                {game.team2GamesWon}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>games won</div>
              {game.team2Players?.length > 0 && (
                <div className={`text-xs ${theme.textMuted} mt-1`}>
                  {game.team2Players.map(p => p.name).join(', ')}
                </div>
              )}
            </div>
          </div>

          {/* Game-by-Game Results */}
          <h4 className={`${theme.textMuted} text-sm font-medium mb-3`}>Game-by-Game Scores</h4>
          <div className="space-y-2">
            {(game.gameResults || []).map((gameResult) => {
              const gameWinnerIsTeam1 = gameResult.winnerTeam === 1;
              return (
                <div
                  key={gameResult.id}
                  className={`grid grid-cols-3 gap-2 p-3 rounded-lg ${theme.backgroundSecondary}`}
                >
                  <div className={`text-center font-semibold ${gameWinnerIsTeam1 ? 'text-green-600' : theme.text}`}>
                    {gameResult.team1Score}
                  </div>
                  <div className={`text-center text-sm ${theme.textMuted}`}>
                    Game {gameResult.gameNumber}
                  </div>
                  <div className={`text-center font-semibold ${!gameWinnerIsTeam1 ? 'text-green-600' : theme.text}`}>
                    {gameResult.team2Score}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Player Statistics */}
        {Object.keys(stats).length > 0 && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
            <h3 className={`${theme.text} font-semibold mb-4`}>Player Statistics</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={theme.textMuted}>
                    <th className="text-left py-2">Player</th>
                    <th className="text-center py-2">Pts Serving</th>
                    <th className="text-center py-2">Rallies</th>
                    <th className="text-center py-2">Win Shots</th>
                    <th className="text-center py-2">Eff %</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(stats).map((stat) => (
                    <tr key={stat.name} className={`${theme.text} border-t ${theme.border}`}>
                      <td className="py-2">{stat.name}</td>
                      <td className="text-center py-2">{stat.pointsWhileServing}</td>
                      <td className="text-center py-2">{stat.ralliesServed}</td>
                      <td className="text-center py-2">{stat.totalWinningShots}</td>
                      <td className="text-center py-2">{stat.servingEfficiency}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Rally History */}
        {totalRallies > 0 && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
            <h3 className={`${theme.text} font-semibold mb-4`}>
              Rally History ({totalRallies} total rallies)
            </h3>

            {/* Each game as a collapsible section */}
            <div className="space-y-3">
              {(game.matches || []).filter(m => m.status === 'completed').map((match) => {
                const matchRallies = match.rallies || [];
                if (matchRallies.length === 0) return null;

                const gameWinnerIsTeam1 = match.winnerPlayer === 'A';
                const gameWinnerName = gameWinnerIsTeam1 ? game.team1Name : game.team2Name;

                return (
                  <details
                    key={match.matchNumber}
                    className={`${theme.backgroundSecondary} rounded-lg border ${theme.border}`}
                    open={match.matchNumber === (game.matches?.length || 1)}
                  >
                    <summary className={`p-3 cursor-pointer flex items-center justify-between`}>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${theme.text}`}>Game {match.matchNumber}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${gameWinnerIsTeam1 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                          {gameWinnerName} won
                        </span>
                      </div>
                      <div className={`font-semibold ${theme.text}`}>
                        <span className={gameWinnerIsTeam1 ? 'text-green-600' : ''}>{match.team1Score}</span>
                        <span className={theme.textMuted}> - </span>
                        <span className={!gameWinnerIsTeam1 ? 'text-green-600' : ''}>{match.team2Score}</span>
                      </div>
                    </summary>

                    <div className="px-3 pb-3">
                      {/* Column headers */}
                      <div className={`grid grid-cols-[2rem_1fr_auto] text-xs ${theme.textMuted} py-1 border-b ${theme.border} mb-1 gap-2`}>
                        <span>#</span>
                        <span>Result</span>
                        <span className="text-right">Score</span>
                      </div>

                      {/* Rally rows */}
                      <div className="max-h-60 overflow-y-auto">
                        {matchRallies.map((rally, idx) => {
                          const isTeam1Win = rally.winningTeam === 1;
                          // In rally scoring every win scores; in sideout scoring only the serving team scores
                          const pointScored = !rally.isServiceFault && (rally.winningTeam === rally.servingTeam || game.isRally);
                          const newScore1 = rally.team1ScoreBefore + (pointScored && isTeam1Win ? 1 : 0);
                          const newScore2 = rally.team2ScoreBefore + (pointScored && !isTeam1Win ? 1 : 0);

                          // Look up player names — ids are UUIDs, compare as
                          // strings (parseInt() truncates at the first
                          // non-numeric character and collides between ids
                          // that share a common numeric UUID prefix). Include
                          // full team rosters too, not just the currently
                          // playing team1Players/team2Players — a swapped-out
                          // player's older rallies would otherwise fail this
                          // lookup and get mislabeled with the current player.
                          const allPlayers = [...(game.team1Players || []), ...(game.team2Players || []), ...participantATeamRoster, ...participantBTeamRoster];
                          const findPlayer = (id) => allPlayers.find(p => String(p.id) === String(id))?.name;
                          const winnerPlayer = rally.winningShotPlayerId ? findPlayer(rally.winningShotPlayerId) : null;
                          const errorPlayer = rally.pointLoserPlayerId ? findPlayer(rally.pointLoserPlayerId) : null;
                          const isErrorType = rally.shotType === 'forced_error' || rally.shotType === 'unforced_error' || rally.shotType === 'other';
                          const serverPlayer = rally.serverPlayerId ? findPlayer(rally.serverPlayerId) : null;

                          return (
                            <div
                              key={rally.id}
                              className={`grid grid-cols-[2rem_1fr_auto] text-xs py-1.5 border-b ${theme.border} last:border-0 gap-2`}
                            >
                              <span className={theme.textMuted}>{idx + 1}</span>
                              <div className="min-w-0">
                                <span className={`font-medium ${
                                  rally.isServiceFault
                                    ? 'text-orange-500'
                                    : isTeam1Win ? 'text-green-600' : 'text-blue-600'
                                }`}>
                                  {rally.isServiceFault
                                    ? 'Fault'
                                    : winnerPlayer || (isTeam1Win ? game.team1Name : game.team2Name)}
                                </span>
                                {!rally.isServiceFault && (
                                  <span className={theme.textMuted}>
                                    {isErrorType
                                      ? ` — ${rally.shotType === 'other' ? 'other error' : rally.shotType.replace(/_/g, ' ')}${errorPlayer ? ` by ${errorPlayer}` : ''}`
                                      : winnerPlayer
                                        ? ` — ${winnerPlayer}${rally.shotType ? ` (${rally.shotType.replace(/_/g, ' ')})` : ''}`
                                        : rally.shotType ? ` (${rally.shotType.replace(/_/g, ' ')})` : ''}
                                  </span>
                                )}
                                {serverPlayer && (
                                  <div className={`${theme.textMuted} text-[10px]`}>serve: {serverPlayer}</div>
                                )}
                              </div>
                              <span className={`text-right font-medium ${theme.text} whitespace-nowrap`}>
                                {newScore1}-{newScore2}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Game summary */}
                      <div className={`mt-2 pt-2 border-t ${theme.border} text-xs ${theme.textMuted} flex justify-between`}>
                        <span>{matchRallies.length} rallies played</span>
                        <span>
                          {matchRallies.filter(r => r.winningTeam === 1 && !r.isServiceFault).length} - {matchRallies.filter(r => r.winningTeam === 2 && !r.isServiceFault).length} points
                        </span>
                      </div>
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {/* Complete Event Button */}
          {showCompleteEventButton && (
            <button
              onClick={handleCompleteEvent}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Complete Event & Continue
            </button>
          )}

        </div>
      </div>
    );
  };

  const renderSwapModal = () => {
    if (!swapModal || !game) return null;

    const roster = swapModal.team === 1 ? participantATeamRoster : participantBTeamRoster;
    const currentPlayer = swapModal.oldPlayer;
    const currentlyPlayingIds = (swapModal.team === 1 ? game.team1Players : game.team2Players)?.map(p => String(p.id)) || [];
    // Exclude everyone already on this side (not just the one being swapped
    // out) — in doubles you can't pick your own teammate as your replacement.
    const candidates = roster.filter(p => !currentlyPlayingIds.includes(String(p.id)));

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !swapping && setSwapModal(null)}>
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6 w-full max-w-md`} onClick={e => e.stopPropagation()}>
          <h3 className={`${theme.text} text-lg font-semibold mb-1`}>Swap Player</h3>
          <p className={`text-sm ${theme.textMuted} mb-4`}>
            Currently playing: <span className="font-medium">{currentPlayer?.name || '—'}</span>. Pick a replacement from the same team's roster.
          </p>
          <div className="space-y-2">
            {candidates.length === 0 ? (
              <p className={`text-sm ${theme.textMuted} text-center py-4`}>No other players available on this roster.</p>
            ) : candidates.map(player => (
              <button
                key={player.id}
                onClick={() => handleConfirmSwap(player)}
                disabled={swapping}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${theme.secondary} ${theme.secondaryHover} disabled:opacity-50`}
              >
                {player.name}
              </button>
            ))}
          </div>
          <button
            onClick={() => setSwapModal(null)}
            disabled={swapping}
            className={`w-full mt-4 ${theme.secondary} ${theme.secondaryHover} ${theme.text} py-2 rounded-lg disabled:opacity-50`}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Skip rally-by-rally scoring — type in both teams' final scores and
  // complete the game (and, if it's the deciding game of the series, the
  // event) in one action. Only reachable before any rally has been recorded.
  const renderFinalScoreModal = () => {
    if (!showFinalScoreModal || !game) return null;

    const valid = isFinalScoreValid();

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !finalScoreSubmitting && setShowFinalScoreModal(false)}>
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6 w-full max-w-md`} onClick={e => e.stopPropagation()}>
          <h3 className={`${theme.text} text-lg font-semibold mb-1`}>Enter Final Score</h3>
          <p className={`text-sm ${theme.textMuted} mb-4`}>
            Skips point-by-point scoring for this game. First to {game.scoreToWin}{game.winByTwo ? ', win by 2' : ''}.
          </p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className={`block text-xs font-semibold mb-1 uppercase tracking-wide text-green-500 truncate`}>
                {game.team1Name}
              </label>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={finalScoreInputs.a}
                onChange={e => setFinalScoreInputs(prev => ({ ...prev, a: e.target.value }))}
                disabled={finalScoreSubmitting}
                className={`w-full px-3 py-2 border rounded-lg text-center text-lg font-semibold ${theme.input}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-semibold mb-1 uppercase tracking-wide text-blue-400 truncate`}>
                {game.team2Name}
              </label>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={finalScoreInputs.b}
                onChange={e => setFinalScoreInputs(prev => ({ ...prev, b: e.target.value }))}
                disabled={finalScoreSubmitting}
                className={`w-full px-3 py-2 border rounded-lg text-center text-lg font-semibold ${theme.input}`}
              />
            </div>
          </div>
          <button
            onClick={handleSubmitFinalScore}
            disabled={!valid || finalScoreSubmitting}
            className="w-full py-3 rounded-lg font-medium transition-colors bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
          >
            {finalScoreSubmitting ? 'Completing…' : 'Complete Game'}
          </button>
          <button
            onClick={() => setShowFinalScoreModal(false)}
            disabled={finalScoreSubmitting}
            className={`w-full mt-2 ${theme.secondary} ${theme.secondaryHover} ${theme.text} py-2 rounded-lg disabled:opacity-50`}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // MLP Dreambreaker forced swap modal — both teams' pickers in one place,
  // non-dismissable (no backdrop-click or Cancel), plus "Undo Last Point" as
  // the only way out besides picking both replacements.
  const renderForcedSwapModal = () => {
    if (!forcedSwapModal || !game) return null;

    const team1CurrentIds = game.team1Players?.map(p => String(p.id)) || [];
    const team2CurrentIds = game.team2Players?.map(p => String(p.id)) || [];
    const team1Candidates = participantATeamRoster.filter(p => !team1CurrentIds.includes(String(p.id)));
    const team2Candidates = participantBTeamRoster.filter(p => !team2CurrentIds.includes(String(p.id)));

    const renderTeamPicker = (teamNum, teamName, oldPlayer, candidates) => {
      const selected = forcedSwapSelections[`team${teamNum}`];
      return (
        <div>
          <div className={`text-sm font-medium ${theme.text} mb-1`}>{teamName}</div>
          <div className={`text-xs ${theme.textMuted} mb-2`}>Currently playing: {oldPlayer?.name || '—'}</div>
          <div className="space-y-1.5">
            {candidates.map(player => (
              <button
                key={player.id}
                onClick={() => setForcedSwapSelections(prev => ({ ...prev, [`team${teamNum}`]: player }))}
                disabled={forcedSwapping}
                className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-colors border ${
                  selected?.id === player.id
                    ? 'border-green-500 bg-green-500/10'
                    : theme.border
                } ${theme.secondary} ${theme.secondaryHover} disabled:opacity-50`}
              >
                {player.name}
              </button>
            ))}
          </div>
        </div>
      );
    };

    const bothSelected = !!forcedSwapSelections.team1 && !!forcedSwapSelections.team2;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6 w-full max-w-lg`}>
          <h3 className={`${theme.text} text-lg font-semibold mb-1`}>Dreambreaker Swap Required</h3>
          <p className={`text-sm ${theme.textMuted} mb-4`}>
            {forcedSwapModal.total} total points reached — both teams must swap in a new player to continue.
          </p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            {renderTeamPicker(1, game.team1Name, forcedSwapModal.team1OldPlayer, team1Candidates)}
            {renderTeamPicker(2, game.team2Name, forcedSwapModal.team2OldPlayer, team2Candidates)}
          </div>
          <button
            onClick={handleConfirmForcedSwap}
            disabled={!bothSelected || forcedSwapping}
            className={`w-full py-3 rounded-lg font-medium transition-colors bg-green-600 hover:bg-green-700 text-white disabled:opacity-50`}
          >
            Confirm Swap
          </button>
          <button
            onClick={handleUndoFromForcedSwap}
            disabled={forcedSwapping}
            className={`w-full mt-2 flex items-center justify-center gap-2 ${theme.secondary} ${theme.secondaryHover} ${theme.text} py-2 rounded-lg disabled:opacity-50`}
          >
            <Undo2 className="w-4 h-4" /> Undo Last Point Instead
          </button>
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleSkipForcedSwapOnce}
              disabled={forcedSwapping}
              className={`flex-1 text-sm py-2 rounded-lg ${theme.textMuted} hover:${theme.text} disabled:opacity-50`}
            >
              Skip This Time
            </button>
            <button
              onClick={handleSkipForcedSwapForever}
              disabled={forcedSwapping}
              className={`flex-1 text-sm py-2 rounded-lg ${theme.textMuted} hover:${theme.text} disabled:opacity-50`}
            >
              Skip Forever (This Game)
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className={`${theme.background} p-4`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="w-5 h-5" />
        <h1 className={`text-xl font-bold ${theme.text}`}>
          {sportType || 'Pickleball'}
        </h1>
        {currentPhase !== 'completed' ? (
          <button
            onClick={handleRefresh}
            disabled={saving}
            className={`${theme.textMuted} disabled:opacity-50`}
            title="Refresh game data"
          >
            <RefreshCw className={`w-5 h-5 ${saving ? 'animate-spin' : ''}`} />
          </button>
        ) : (
          <div className="w-5 h-5" />
        )}
      </div>

      {/* Phase Content */}
      {currentPhase === 'scoring' && renderScoringPhase()}
      {currentPhase === 'completed' && renderCompletedPhase()}

      {/* Modals */}
      {renderSwapModal()}
      {renderFinalScoreModal()}
      {renderForcedSwapModal()}
    </div>
  );
};

export default PickleballScoring;
